/**
 * `§5.35`: **a header the client sends is a contract the api's CORS must sign.**
 *
 * ## What this file is for, and what it cost to learn
 *
 * `P0-IDN-063` added `x-hakili-surface` to every call `@hakili/api-client`
 * makes, so the api could name the surface a request came from. `cors.ts`
 * carried its own hand-written `allowedHeaders`, and nothing joined the two.
 * The deploy of `#292` therefore made staging **unusable**: every login on
 * every application failed at the browser's preflight, with
 *
 *   « Request header field x-hakili-surface is not allowed by
 *     Access-Control-Allow-Headers in preflight response »
 *
 * on the console, and « Failed to fetch » on dealer-web , the same fault
 * wearing two sentences.
 *
 * **Every gate was green.** The integration tier calls the api with `fetch`
 * from node, and node does not preflight; the unit tiers stub `fetch`
 * entirely. So no witness this repository had could see it, and none could
 * have: the missing check was not a test but a shared NAME.
 *
 * ## Why the list lives here
 *
 * `packages/contracts` is the only place both sides may read , the client is a
 * package, the CORS configuration is inside `platform`, and 9.3.1 forbids
 * either reaching the other. Same reason as the error registry and the right
 * keys beside it.
 *
 * ## What belongs in it
 *
 * **Only headers a browser would PREFLIGHT.** `accept`, `accept-language` and
 * `content-type` with a form or text value are CORS-safelisted and need no
 * permission; `content-type: application/json` is not safelisted and does. A
 * header listed here that nobody sends is dead permission, and a header sent
 * and not listed is what this file exists to prevent , `cors-contract.spec.ts`
 * refuses both directions.
 */
export const CLIENT_REQUEST_HEADERS = [
  /** The bearer token, on every authenticated call. */
  "Authorization",
  /** `application/json` is not CORS-safelisted, so every call with a body preflights. */
  "Content-Type",
  /** `P0-PLT-025`'s replay key, on the calls that mutate. */
  "Idempotency-Key",
  /** Optimistic concurrency on the routes that take it. */
  "If-Match",
  /**
   * `P0-IDN-063`, `00-SPINE` 6.6: which surface the application IS.
   *
   * The header that broke staging. It travels on EVERY call, not only the auth
   * ones, because the api names a surface wherever it needs one.
   */
  "X-Hakili-Surface",
] as const;

export type ClientRequestHeader = (typeof CLIENT_REQUEST_HEADERS)[number];

/** The surface declaration's name, lowercased as node delivers it. */
export const SURFACE_HEADER = "x-hakili-surface";
